import * as THREE from 'three';
import { VoxelModelLoader } from './voxelLoader';
import { obstacles } from './world';

export interface MapLoadOptions {
  scale?: number;
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
}

/**
 * Load a map from a model file and add collision detection
 */
export async function loadMap(
  path: string,
  scene: THREE.Scene,
  options: MapLoadOptions = {}
): Promise<THREE.Group> {
  console.log(`[MapLoader] Loading map from: ${path}`);

  const {
    scale = 1.0,
    position = new THREE.Vector3(0, 0, 0),
    rotation = new THREE.Euler(0, 0, 0)
  } = options;

  // Determine file type from extension
  const ext = path.split('.').pop()?.toLowerCase() || 'glb';
  const type = ext as 'obj' | 'dae' | 'vox' | 'gltf' | 'glb';
  console.log(`[MapLoader] File type detected: ${type}`);

  try {
    // Load the model
    const loader = VoxelModelLoader.getInstance();
    console.log(`[MapLoader] Starting model load...`);
    const mapGroup = await loader.loadModel(path, type, scale);
    console.log(`[MapLoader] Model loaded successfully, children:`, mapGroup.children.length);

    // Apply position and rotation
    mapGroup.position.copy(position);
    mapGroup.rotation.copy(rotation);

    // Add to scene
    scene.add(mapGroup);
    console.log(`[MapLoader] Map added to scene`);

    // Generate collision boxes for all meshes in the map
    addMapCollision(mapGroup);

    return mapGroup;
  } catch (error) {
    console.error(`[MapLoader] Failed to load map:`, error);
    throw error;
  }
}

/**
 * Generate collision boxes for all meshes in a map group
 */
function addMapCollision(group: THREE.Group) {
  // CRITICAL: Update all world matrices first
  group.updateMatrixWorld(true);

  group.traverse((child) => {
    if (child instanceof THREE.Mesh && child.geometry) {
      // Compute bounding box for this mesh
      if (!child.geometry.boundingBox) {
        child.geometry.computeBoundingBox();
      }

      const boundingBox = child.geometry.boundingBox;
      if (!boundingBox) return;

      // Transform the bounding box to world space
      const worldBox = boundingBox.clone();
      worldBox.applyMatrix4(child.matrixWorld);

      // Store in obstacles map for collision detection
      const id = `map_${child.uuid}`;
      obstacles.set(id, {
        type: 'map',
        bounds: worldBox
      });

      console.log(`Added collision for ${id}:`, worldBox);
    }
  });

  console.log(`Total map collision boxes: ${Array.from(obstacles.keys()).filter(k => k.startsWith('map_')).length}`);
}

// Removed - Nuketown requires a real 3D model file (.glb/.vox)

import { loadGLBMap } from './maps/loadGLBMap';
import { PhysicsManager } from './physics';

/**
 * Load map based on selected type
 * 
 * FORMATS EXPLAINED:
 * - Procedural: Generated using THREE.js primitives (BoxGeometry, etc) in code
 * - GLB/GLTF: Standard 3D model formats (place .glb files in frontend/public/maps/)
 * - Voxel (.vox): MagicaVoxel format - actual 3D voxel model files
 * 
 * To add GLB maps:
 * 1. Download .glb files from Sketchfab (filter by "Downloadable")
 * 2. Place in frontend/public/maps/
 * 3. Add case for your map below
 */
export async function loadSelectedMap(
  mapType: string,
  scene: THREE.Scene,
  makeChunk?: (scene: THREE.Scene, dimensions: [number, number, number], seed: number) => void,
  physicsManager?: PhysicsManager
): Promise<THREE.Group | null> {
  // Clear any existing terrain/obstacles before loading new map
  obstacles.clear();
  
  // Check for GLB maps
  if (mapType === 'fps-arena') {
    try {
      return await loadGLBMap('fps-map-low-poly.glb', scene, physicsManager);
    } catch (error) {
      console.error('Failed to load fps-map-low-poly.glb:', error);
    }
  }
  
  // Procedural terrain (always works, no files needed)
  if (mapType === 'procedural' && makeChunk) {
    makeChunk(scene, [64, 64, 16], 1337, physicsManager);
    return null; 
  }
  
  // Default to procedural if map doesn't exist
  console.warn(`Map type "${mapType}" not found, using procedural terrain`);
  if (makeChunk) {
    makeChunk(scene, [64, 64, 16], 1337, physicsManager);
  }
  return null;
}
